import React, { useEffect, useState, useMemo, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  BottomNavigation,
  BottomNavigationAction,
  Box,
  Button,
  Snackbar,
  Alert,
  Avatar,
  Menu,
  MenuItem,
} from '@mui/material';
import DynamicFeedRoundedIcon from '@mui/icons-material/DynamicFeedRounded';
import TravelExploreRoundedIcon from '@mui/icons-material/TravelExploreRounded';
import FilterAltRoundedIcon from '@mui/icons-material/FilterAltRounded';
import PersonIcon from '@mui/icons-material/Person';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import WorkspacesOutlinedIcon from '@mui/icons-material/WorkspacesOutlined';
import {
  Link as RouterLink,
  useNavigate,
  useLocation,
} from 'react-router-dom';
import { useStocksData } from '../../../hooks/stocksHooks/useStocksData';
import SearchBar from './SearchBar/SearchBar';
import Palette from '../../../resources/UtilityJsons/Palettes.json';
import { useAuth } from '../../../context/AuthContext';
import './Header.css';

const NotificationBell = React.lazy(
  () => import('../NotificationBell/NotificationBell')
);

const LoginSignUpDialog = React.lazy(
  () => import('../LoginSignUpDialog/LoginSignUpDialog')
);
const homeImage = require('../../../resources/Assets/InsicardLogos/Optimized/InsicardHeaderLogo.png');
const homeImageShort = require('../../../resources/Assets/InsicardLogos/Optimized/InsicardIconLogo.png');

interface NavItem {
  name: string;
  path: string;
  icon: JSX.Element;
}

const TopBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Dati di autenticazione dal contesto
  const { isLoggedIn, user, logout } = useAuth();

  // The full stock index is only needed after the user interacts with search.
  const [shouldLoadStocks, setShouldLoadStocks] = useState(false);
  const { data: tickers = [] } = useStocksData(shouldLoadStocks);
  // Stati per la logica di layout e menu
  const [lastClicked, setLastClicked] = useState<string>('');
  const sizeThreshold = 1080;
  const [isLargeScreen, setIsLargeScreen] = useState<boolean>(
    window.innerWidth > sizeThreshold
  );

  // Stato per la modale di login e per lo snackbar
  const [openDialog, setOpenDialog] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Stato per gestire il menu dell'Avatar
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Heights (px)
  const MOBILE_HEADER_HEIGHT = 64;
  const DESKTOP_HEADER_HEIGHT = 64;
  const BOTTOM_NAV_HEIGHT = 56; // base, safe-area added via CSS env()

  const getHeaderHeights = useCallback(() => {
    const isMobileView = !isLargeScreen;
    return {
      expanded: isMobileView ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT,
      collapsed: isMobileView ? MOBILE_HEADER_HEIGHT : DESKTOP_HEADER_HEIGHT,
    };
  }, [isLargeScreen]);

  // Nessuna fetch locale: i dati arrivano dal cache condiviso (useStocksData)

  // Elementi di navigazione (memoized)
  const navigationElements: NavItem[] = useMemo(
    () => [
      { name: 'Feed', path: '/feed', icon: <DynamicFeedRoundedIcon /> },
      { name: 'Explore', path: '/explore', icon: <TravelExploreRoundedIcon /> },
      { name: 'My Space', path: '/my-space', icon: <WorkspacesOutlinedIcon /> },
      { name: 'Screener', path: '/screener', icon: <FilterAltRoundedIcon /> },
    ],
    []
  );

  // Gestione resize per top/bottom navigation
  useEffect(() => {
    const handleResize = () => {
      setIsLargeScreen(window.innerWidth > sizeThreshold);
    };
    const mediaQuery = window.matchMedia('(min-width: 1081px)');
    mediaQuery.addEventListener('change', handleResize);
    handleResize();
    return () => {
      mediaQuery.removeEventListener('change', handleResize);
    };
  }, [sizeThreshold]);

  // Set/update CSS variables for layout spacing
  useEffect(() => {
    const { expanded, collapsed } = getHeaderHeights();
    const headerCurrent = expanded;
    document.documentElement.style.setProperty(
      '--app-header-expanded-height',
      `${expanded}px`
    );
    document.documentElement.style.setProperty(
      '--app-header-collapsed-height',
      `${collapsed}px`
    );
    document.documentElement.style.setProperty(
      '--app-header-current-height',
      `${headerCurrent}px`
    );

    const bottomHeight = !isLargeScreen ? BOTTOM_NAV_HEIGHT : 0;
    document.documentElement.style.setProperty(
      '--app-bottom-nav-height',
      `${BOTTOM_NAV_HEIGHT}px`
    );
    document.documentElement.style.setProperty(
      '--app-bottom-nav-current-height',
      `${bottomHeight}px`
    );
  }, [isLargeScreen, getHeaderHeights]);

  useEffect(() => {
    const currentSegment = (
      location.pathname.split('/')[1] || ''
    ).toLowerCase();
    const normalize = (p: string) => {
      const seg = p.replace(/^\/?/, '').split('/')[0];
      return (seg || '').toLowerCase();
    };
    const matchingElement = navigationElements.find(
      item => normalize(item.path) === currentSegment
    );
    if (matchingElement && lastClicked !== matchingElement.name) {
      setLastClicked(matchingElement.name);
    }
  }, [location.pathname, lastClicked, navigationElements]);

  const warmStocksForNavigation = (item: NavItem) => {
    if (item.name === 'Screener') {
      setShouldLoadStocks(true);
    }
  };

  const handleNavIntent = (item: NavItem) => {
    warmStocksForNavigation(item);
    setLastClicked(item.name);
  };

  // Elementi di navigazione (spostati sopra)

  // State for menu anchor element
  // const open = Boolean(anchorEl);

  // Apertura/chiusura modale di login
  const handleOpenDialog = () => {
    setOpenDialog(true);
  };
  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  // Snackbar
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  // Gestione Avatar
  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  // Funzione per estrarre iniziali dal displayName
  const getInitials = (name?: string): string | null => {
    if (!name || name.trim() === '') return null;
    return name
      .split(' ')
      .map(n => n[0])
      .join('');
  };

  // Funzione di logout (chiama direttamente "logout" dal contesto)
  const handleLogout = () => {
    logout(); // rimuove token e user dal contesto/localStorage
    setSnackbarMessage('Logged out successfully');
    setSnackbarOpen(true);
    setAnchorEl(null);
    navigate('/', { replace: true });
  };

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          background:
            'linear-gradient(90deg, rgba(8, 72, 60, 0.98), rgba(13, 104, 85, 0.98))',
          backdropFilter: 'blur(18px)',
          borderBottom: 0,
          boxShadow: '0 8px 24px rgba(5, 45, 38, 0.14)',
          zIndex: 1300,
        }}
      >
        <Toolbar
          sx={{
            maxWidth: '1504px',
            width: '100%',
            margin: '0 auto',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: { xs: 0.75, md: 1.5 },
            px: { xs: 1.25, sm: 2, md: 3 },
            height: `var(--app-header-current-height, 64px)`,
            minHeight: `var(--app-header-current-height, 56px)`,
            maxHeight: `var(--app-header-current-height, 64px)`,
            overflow: 'hidden',
          }}
        >
          {/* LOGO */}
          <Box
            component={RouterLink}
            to="/explore"
            aria-label="Insicard home"
            sx={{ display: 'inline-flex', flexShrink: 0 }}
          >
            <picture className="HeaderLogoPicture">
              <source
                media="(max-width: 768px)"
                srcSet={homeImageShort}
                width="160"
                height="160"
              />
              <img
                src={homeImage}
                alt=""
                className="HeaderLogo"
                width="512"
                height="93"
                decoding="async"
              />
            </picture>
          </Box>

          {/* NAVIGATION */}
          {isLargeScreen && (
            <List
              sx={{
                display: 'flex',
                flexDirection: 'row',
                gap: 0.5,
                p: 0,
              }}
            >
              {navigationElements.map(item => (
                <ListItem key={item.name} disablePadding>
                  <ListItemButton
                    component={RouterLink}
                    to={item.path}
                    className="nav-button"
                    sx={{
                      textAlign: 'center',
                      display: 'flex',
                      gap: 0.5,
                      bgcolor:
                        lastClicked === item.name
                          ? 'rgba(255,255,255,0.13)'
                          : 'transparent',
                      border: '1px solid',
                      borderColor:
                        lastClicked === item.name
                          ? 'rgba(255,255,255,0.13)'
                          : 'transparent',
                        borderRadius: 2.25,
                        px: 1.45,
                        py: 0.75,
                      transition:
                        'background-color 150ms ease, border-color 150ms ease',
                      '&:hover': {
                        bgcolor: 'rgba(255,255,255,0.09)',
                        borderColor: 'rgba(255,255,255,0.08)',
                      },
                    }}
                    onMouseEnter={() => warmStocksForNavigation(item)}
                    onFocus={() => warmStocksForNavigation(item)}
                    onClick={() => handleNavIntent(item)}
                  >
                    <ListItemIcon
                      sx={{
                        minWidth: 'unset',
                        color: Palette.UtilityColor.primary,
                      }}
                    >
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.name}
                      primaryTypographyProps={{
                        fontFamily:
                          'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
                        fontSize: '0.95rem',
                        letterSpacing: '-0.01em',
                        fontWeight: 760,
                        color: Palette.UtilityColor.primary,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {/* SEARCH BAR */}
          <Box
            className="searchBar"
            sx={{
              flex: { xs: '1 1 auto', sm: '0 0 auto' },
              mx: { xs: 0.5, sm: 1.5, md: 2 },
              minWidth: 0,
              width: { xs: 'auto', sm: 330, lg: 360 },
            }}
          >
            <SearchBar
              data={tickers}
              onIntent={() => setShouldLoadStocks(true)}
            />
          </Box>

          {/* LOGIN / AVATAR */}
          {isLoggedIn && (
            <React.Suspense fallback={null}>
              <NotificationBell />
            </React.Suspense>
          )}
          {!isLoggedIn ? (
            <Button
              variant="contained"
              onClick={handleOpenDialog}
              sx={{
                ml: { xs: 0.25, sm: 1 },
                minWidth: { xs: 60, sm: 72 },
                height: { xs: 36, sm: 38 },
                px: { xs: 1, sm: 1.5 },
                borderRadius: 2.25,
                bgcolor: '#f5d46b',
                color: '#173b34',
                boxShadow: 'none',
                textTransform: 'none',
                whiteSpace: 'nowrap',
                fontSize: { xs: '0.72rem', sm: '0.82rem' },
                fontWeight: 850,
                '&:hover': {
                  bgcolor: '#ffe180',
                  boxShadow: '0 7px 18px rgba(0,0,0,0.18)',
                },
              }}
            >
              Log in
            </Button>
          ) : (
            <Avatar
              onClick={handleAvatarClick}
              sx={{
                marginLeft: 2,
                cursor: 'pointer',
                bgcolor: 'primary.main',
              }}
            >
              {getInitials(user?.displayName) || <PersonIcon />}
            </Avatar>
          )}
        </Toolbar>
      </AppBar>

      {!isLargeScreen && (
        <BottomNavigation
          showLabels
          value={lastClicked}
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            background:
              'linear-gradient(90deg, rgba(8, 72, 60, 0.99), rgba(13, 104, 85, 0.99))',
            backdropFilter: 'blur(18px)',
            height: `calc(var(--app-bottom-nav-height, 56px) + env(safe-area-inset-bottom, 0px))`,
            pb: 'env(safe-area-inset-bottom, 0px)',
            borderTop: '1px solid rgba(255,255,255,0.12)',
            boxShadow: '0 -8px 24px rgba(3,35,29,0.16)',
            zIndex: 1300,
          }}
        >
          {navigationElements.map(item => (
            <BottomNavigationAction
              key={item.name}
              value={item.name}
              label={item.name}
              icon={item.icon}
              component={RouterLink}
              to={item.path}
              onTouchStart={() => warmStocksForNavigation(item)}
              onClick={() => handleNavIntent(item)}
              sx={{
                color: 'rgba(255,255,255,0.9)',
                minWidth: 0,
                pt: 0.65,
                '&.Mui-selected': {
                  color: '#f5d46b',
                },
                transition: 'none',
                '& .MuiBottomNavigationAction-label': {
                  fontSize: '0.7rem',
                  lineHeight: 1.1,
                  fontWeight: 700,
                  transition: 'none',
                },
                '&.Mui-selected .MuiBottomNavigationAction-label': {
                  fontWeight: 850,
                },
                '& .MuiSvgIcon-root': { fontSize: 21 },
              }}
            />
          ))}
        </BottomNavigation>
      )}

      {/* MODALE DI LOGIN */}
      {openDialog && (
        <React.Suspense fallback={null}>
          <LoginSignUpDialog open={openDialog} onClose={handleCloseDialog} />
        </React.Suspense>
      )}

      {/* MENU DELL'AVATAR */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            handleCloseMenu();
            navigate('/my-analyses');
          }}
        >
          <AssessmentOutlinedIcon sx={{ mr: 1.25, fontSize: 20 }} />
          My analyses
        </MenuItem>
        <MenuItem onClick={handleLogout}>Logout</MenuItem>
      </Menu>

      {/* SNACKBAR */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="success"
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default TopBar;
